import { useMemo, useState } from "react";
import moment from "moment";
import useSubcategories, { useCategories } from "../graphql/useSubcategories";

export function useTimezone() {
  return useMemo(() => moment().format("Z"), []);
}

export function currencyFormat(num) {
  const formatter = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });
  if (isNaN(num)) {
    return "0,00 €";
  }
  return formatter.format(num);
}

export function convertCurrencyStringToNumber(currencyString) {
  const cleanedString = currencyString.replace(/[^\d,-]/g, "").trim();
  const normalizedString = cleanedString.replace(",", ".");
  const number = parseFloat(normalizedString);
  return number;
}

export const useCategoryAndSubcategorySelector = (subcategoryId) => {
  const { categories, categoryMap, loading: loading1 } = useCategories();
  const {
    subcategories,
    subcategoryMap,
    loading: loading2,
  } = useSubcategories();

  const initialCategoryId = useMemo(() => {
    // initial values for category, subcategory
    if (subcategoryId && subcategoryMap[subcategoryId]) {
      return subcategoryMap[subcategoryId].categoryId;
    }
    return undefined;
  }, [subcategoryId, subcategoryMap]);

  const [categoryId, setCategoryId] = useState(initialCategoryId);

  const subcategoryOptions = useMemo(() => {
    if (!categoryId) {
      return [];
    }
    return subcategories
      .filter((subcategory) => subcategory.categoryId === categoryId)
      .sort((cat1, cat2) => cat1.name.localeCompare(cat2.name));
  }, [categoryId, subcategories]);

  return {
    categoryId,
    setCategoryId,
    subcategoryOptions,
    categories,
    subcategories,
    categoryMap,
    loading: loading2 || loading1,
  };
};

export const usePaginatedData = (
  data,
  pagination,
  sorting,
  mapper,
  sorters,
  rowRenderer
) =>
  useMemo(() => {
    const sortedData = [...data].sort((a, b) => {
      const getValue = (obj, path) =>
        path.split(".").reduce((o, p) => o?.[p], obj);

      const valA = getValue(a, sorting.columnId);
      const valB = getValue(b, sorting.columnId);

      const isEmpty = (val) => val === null || val === undefined || val === "";

      if (isEmpty(valA) && isEmpty(valB)) return 0;
      if (isEmpty(valA)) return 1;
      if (isEmpty(valB)) return -1;

      if (sorters && sorters[sorting.columnId]) {
        return sorters[sorting.columnId](a, b) * sorting.order;
      }

      const strA = String(valA);
      const strB = String(valB);

      if (isNumberString(strA) && isNumberString(strB)) {
        return compareNumberStrings(strA, strB) * sorting.order;
      }

      return (
        strA.localeCompare(strB, undefined, { numeric: true }) * sorting.order
      );
    });

    return sortedData
      .slice(
        pagination.page * pagination.itemsPerPage,
        (pagination.page + 1) * pagination.itemsPerPage
      )
      .map((item) => ({
        element: item,
        id: item._id,
        columns: mapper && mapper(item),
        rowRenderer: rowRenderer && rowRenderer(item),
      }));
  }, [
    data,
    mapper,
    pagination.itemsPerPage,
    pagination.page,
    sorters,
    sorting.columnId,
    sorting.order,
    rowRenderer,
  ]);

// Helper function to check if a string is a valid number
const isNumberString = (str) => /^[0-9]+$/.test(str);

// Helper function to compare strings containing numbers
const compareNumberStrings = (str1, str2) => {
  const num1 = parseInt(str1);
  const num2 = parseInt(str2);

  if (num1 < num2) return -1;
  if (num1 > num2) return 1;
  return 0;
};

// export const usePaginatedData = (
//   data,
//   pagination,
//   sorting,
//   mapper,
//   sorters
// ) => {
//   return useMemo(() => {
//     return data
//       .sort((a, b) => {
//         // contemplate null/0
//         if (!a[sorting.columnId] && !b[sorting.columnId]) return 0;
//         if (a[sorting.columnId] && !b[sorting.columnId]) return 1;
//         if (!a[sorting.columnId] && b[sorting.columnId]) return -1;
//         // both values are set
//         if (sorters && sorters[sorting.columnId]) {
//           // custom sorter defined,
//           return sorters[sorting.columnId](a, b) * sorting.order;
//         }

//         if (
//           typeof a[sorting.columnId] === "string" &&
//           typeof b[sorting.columnId] === "string"
//         ) {
//           const val1 = a[sorting.columnId] || "";
//           const val2 = b[sorting.columnId] || "";
//           return val1.localeCompare(val2) * sorting.order;
//         } else {
//           // check nan
//           if (isNaN(a[sorting.columnId]) && isNaN(b[sorting.columnId]))
//             return 0;
//           if (!isNaN(a[sorting.columnId]) && isNaN(b[sorting.columnId]))
//             return 1;
//           if (isNaN(a[sorting.columnId]) && !isNaN(b[sorting.columnId]))
//             return -1;
//           return (
//             (parseFloat(a[sorting.columnId]) -
//               parseFloat(b[sorting.columnId])) *
//             sorting.order
//           );
//         }
//       })
//       .slice(
//         pagination.page * pagination.itemsPerPage,
//         (pagination.page + 1) * pagination.itemsPerPage
//       )
//       .map((item) => ({
//         element: item,
//         id: item._id,
//         columns: mapper(item),
//       }));
//   }, [
//     data,
//     mapper,
//     pagination.itemsPerPage,
//     pagination.page,
//     sorters,
//     sorting.columnId,
//     sorting.order,
//   ]);
// };

export const buildUserName = (user) => {
  if (!user.name && !user.lastName) return "unnamed";
  if (!user.lastName) return user.name || "unnamed";
  return `${user.name} ${user.lastName}`;
};
